import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Menu, X, User, LogOut, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth, API } from "../App";

/**
 * Shared top navigation used across public marketing pages (Home, Venice
 * Hotels list, Venice Hotel Detail, etc.). It matches the HomePage nav so the
 * user experience is consistent across the site.
 *
 * Anchor links (#fleet, #booking) resolve on the homepage — when the user is
 * on another page we prefix with "/" so they land on the homepage anchor.
 */
export default function SiteNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [routesDropdownOpen, setRoutesDropdownOpen] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    let ignore = false;
    Promise.all([
      axios.get(`${API}/route-categories`).catch(() => ({ data: [] })),
      axios.get(`${API}/booking-settings`).catch(() => ({ data: {} })),
    ]).then(([catsRes, settingsRes]) => {
      if (ignore) return;
      const list = catsRes.data || [];
      // When the Hotel-in-Venice feature is enabled, expose the dedicated
      // landing page (/hotels-in-venice) as the FIRST submenu item so it
      // stays discoverable next to the route categories.
      if (settingsRes.data?.hotel_in_venice_enabled) {
        list.unshift({
          slug: "__hotels_in_venice__",
          name: "Hotels in Venice",
          _href: "/hotels-in-venice",
        });
      }
      setCategories(list);
    });
    return () => {
      ignore = true;
    };
  }, []);

  const isHome = location.pathname === "/";
  const anchor = (hash) => (isHome ? hash : `/${hash}`);

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/80 backdrop-blur-md border-b border-white/5"
      data-testid="site-nav"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="site-nav-logo">
            <div className="text-2xl font-bold logo-text">TN</div>
            <span className="hidden sm:block text-white/60 text-xs tracking-[0.3em] uppercase">
              TaxiNearMe
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <div
              className="relative"
              onMouseEnter={() => setRoutesDropdownOpen(true)}
              onMouseLeave={() => setRoutesDropdownOpen(false)}
              data-testid="nav-routes-wrapper"
            >
              <Link
                to="/routes"
                className="nav-link inline-flex items-center gap-1"
                data-testid="nav-routes"
              >
                All Routes
                {categories.length > 0 && (
                  <ChevronDown className="w-3 h-3 opacity-70" aria-hidden="true" />
                )}
              </Link>
              {routesDropdownOpen && categories.length > 0 && (
                <div
                  className="absolute left-0 top-full pt-3 min-w-[220px] z-50"
                  data-testid="nav-routes-dropdown"
                >
                  <div className="bg-[#0A0A0A] border border-[#C5A059]/30 rounded-sm shadow-2xl py-2">
                    <Link
                      to="/routes"
                      className="block px-4 py-2 text-white/70 hover:text-[#C5A059] hover:bg-white/5 text-sm uppercase tracking-widest text-[10px] border-b border-white/5 mb-1"
                      onClick={() => setRoutesDropdownOpen(false)}
                      data-testid="nav-routes-dropdown-all"
                    >
                      All routes
                    </Link>
                    {categories.map((c) => (
                      <Link
                        key={c.slug}
                        to={c._href || `/routes/category/${c.slug}`}
                        className="block px-4 py-2 text-white/80 hover:text-[#C5A059] hover:bg-white/5 text-sm"
                        onClick={() => setRoutesDropdownOpen(false)}
                        data-testid={`nav-routes-dropdown-${c.slug}`}
                      >
                        {c.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <a href={anchor("#fleet")} className="nav-link" data-testid="nav-fleet">
              Our Fleet
            </a>
            <a href={anchor("#booking")} className="nav-link" data-testid="nav-booking">
              Book Now
            </a>
            <Link to="/why-choose-us" className="nav-link" data-testid="nav-why-us">
              Why Us
            </Link>
            <Link to="/faq" className="nav-link" data-testid="nav-faq">
              FAQ
            </Link>
            {user ? (
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  onClick={() => navigate("/dashboard")}
                  className="text-white/70 hover:text-black hover:bg-[#C5A059]"
                  data-testid="nav-dashboard"
                >
                  <User className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
                {(user.role === "admin" || user.role === "super_admin") && (
                  <Button
                    variant="ghost"
                    onClick={() => navigate("/admin")}
                    className="text-white/70 hover:text-black hover:bg-[#C5A059]"
                    data-testid="nav-admin"
                  >
                    Admin
                  </Button>
                )}
                <Button
                  variant="ghost"
                  onClick={logout}
                  className="text-white/70 hover:text-red-400"
                  data-testid="nav-logout"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => navigate("/login")}
                className="btn-outline-gold"
                data-testid="nav-login"
              >
                Sign In
              </Button>
            )}
          </div>

          <button
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-[#0A0A0A] border-t border-white/5 py-4">
          <div className="flex flex-col gap-4 px-6">
            <Link
              to="/routes"
              className="nav-link py-2"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-routes"
            >
              All Routes
            </Link>
            {categories.length > 0 && (
              <div className="pl-4 -mt-2 mb-1 flex flex-col gap-2 border-l border-[#C5A059]/30">
                {categories.map((c) => (
                  <Link
                    key={c.slug}
                    to={c._href || `/routes/category/${c.slug}`}
                    className="text-white/60 hover:text-[#C5A059] text-sm"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-category-${c.slug}`}
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
            )}
            <a
              href={anchor("#fleet")}
              className="nav-link py-2"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-fleet"
            >
              Our Fleet
            </a>
            <a
              href={anchor("#booking")}
              className="nav-link py-2"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-booking"
            >
              Book Now
            </a>
            <Link
              to="/why-choose-us"
              className="nav-link py-2"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-why-us"
            >
              Why Us
            </Link>
            <Link
              to="/faq"
              className="nav-link py-2"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-faq"
            >
              FAQ
            </Link>
            {user ? (
              <>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate("/dashboard");
                  }}
                  className="justify-start text-white/70 hover:text-black hover:bg-[#C5A059]"
                >
                  Dashboard
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    logout();
                  }}
                  className="justify-start text-red-400"
                >
                  Sign Out
                </Button>
              </>
            ) : (
              <Button
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate("/login");
                }}
                className="btn-gold"
              >
                Sign In / Register
              </Button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
